// Sky dome and confetti (§12.5 Common): a radius-200 BackSide sphere with the map's gradient, slow
// fbm clouds (or twinkling stars on the night-time Pinball table) and a soft glow toward the key
// light, plus a layer of drifting confetti quads, animated entirely in the vertex shader (one
// InstancedMesh, one draw call).
import {
  BackSide, Color, DoubleSide, InstancedBufferAttribute, InstancedMesh, Matrix4, Mesh, PlaneGeometry, ShaderMaterial, SphereGeometry, UniformsLib,
  UniformsUtils, Vector2, Vector3, type ColorRepresentation,
} from 'three';
import type { QualityTier } from '../../Renderer';
import { GLSL_NOISE } from '../kit/glsl';
import type { EnvUniforms } from '../kit/materials';
import { seeded } from '../kit/geometry';

export const SKY_RADIUS = 200;

export interface SkyStyle {
  top: ColorRepresentation;
  bottom: ColorRepresentation;
  /** Cloud color and coverage 0..1 (0 = none). */
  cloud: ColorRepresentation;
  clouds: number;
  /** Star density 0..1 (night skies). */
  stars: number;
  /** Glow toward the key light. */
  sunGlow: number;
  /** A striped synthwave sun on the horizon behind the arena (night maps), 0 = none. */
  synthSun?: number;
  synthColors?: readonly [ColorRepresentation, ColorRepresentation];
}

const SKY_VERTEX = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize(position);
  vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_Position = vec4(p.xy, p.w * 0.99999, p.w);
}`;

const SKY_FRAGMENT = /* glsl */ `
uniform vec3 uTop;
uniform vec3 uBottom;
uniform vec3 uCloud;
uniform float uClouds;
uniform float uStars;
uniform float uSunGlow;
uniform vec3 uSunDir;
uniform float uTime;
uniform float uSynth;
uniform vec3 uSynthA;
uniform vec3 uSynthB;
varying vec3 vDir;
${GLSL_NOISE}
void main() {
  vec3 d = normalize(vDir);
  float h = d.y;
  vec3 col = mix(uBottom, uTop, smoothstep(-0.02, 0.62, h));
  // Soft halo toward the key light.
  float sun = max(dot(d, uSunDir), 0.0);
  col += uSunGlow * (pow(sun, 24.0) * 0.55 + pow(sun, 4.0) * 0.12) * vec3(1.0, 0.95, 0.85);
  if (uClouds > 0.0 && h > -0.05) {
    vec2 p = d.xz / (h + 0.28) * 1.6 + vec2(uTime * 0.012, uTime * 0.004);
    float n = envFbm(p, 5);
    float c = smoothstep(1.0 - uClouds * 0.55, 1.05 - uClouds * 0.3, n + 0.12);
    float lit = smoothstep(0.35, 0.9, envFbm(p + vec2(0.35, 0.2), 3));
    vec3 cloudCol = mix(uCloud * 0.86 + uBottom * 0.14, uCloud, lit);
    col = mix(col, cloudCol, c * smoothstep(-0.05, 0.25, h) * 0.9);
  }
  if (uStars > 0.0 && h > 0.0) {
    vec2 g = d.xz / (h + 0.4) * 60.0;
    vec2 cell = floor(g);
    float rnd = envHash12(cell);
    vec2 sp = envHash22(cell) - 0.5;
    float star = smoothstep(0.12, 0.0, length(fract(g) - 0.5 - sp * 0.6)) * step(1.0 - uStars * 0.25, rnd);
    float tw = 0.6 + 0.4 * sin(uTime * (2.0 + rnd * 4.0) + rnd * 40.0);
    col += star * tw * smoothstep(0.0, 0.3, h) * vec3(0.9, 0.85, 1.0);
  }
  if (uSynth > 0.0) {
    // Retro sun low over the horizon behind the arena (three.js −z): a gradient disc whose lower
    // half is cut into bands that thin toward the top, with a soft glow around it.
    vec2 sp = vec2(atan(d.x, -d.z), asin(clamp(d.y, -1.0, 1.0)) - 0.09);
    float R = 0.27;
    float dist = length(sp);
    float t = clamp((sp.y + R) / (2.0 * R), 0.0, 1.0);
    vec3 sunCol = mix(uSynthA, uSynthB, smoothstep(0.0, 1.0, t));
    float band = fract(sp.y * 26.0 + uTime * 0.25);
    float cut = step(sp.y, 0.02) * step(band, 0.3 + 0.55 * clamp(-sp.y / R, 0.0, 1.0) * 0.6);
    float disc = (1.0 - smoothstep(R - 0.004, R + 0.004, dist)) * (1.0 - cut);
    col += uSynthA * exp(-max(dist - R, 0.0) * 9.0) * 0.35 * uSynth;
    col = mix(col, sunCol * 1.15, disc * uSynth);
  }
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}`;

export function createSkyDome(style: SkyStyle, env: EnvUniforms, sunDir: Vector3): { mesh: Mesh; dispose(): void } {
  const mat = new ShaderMaterial({
    uniforms: {
      uTop: { value: new Color(style.top) }, uBottom: { value: new Color(style.bottom) }, uCloud: { value: new Color(style.cloud) },
      uClouds: { value: style.clouds }, uStars: { value: style.stars }, uSunGlow: { value: style.sunGlow },
      uSunDir: { value: sunDir.clone().normalize() }, uTime: env.uTime,
      uSynth: { value: style.synthSun ?? 0 }, uSynthA: { value: new Color(style.synthColors?.[0] ?? '#FF3EC8') }, uSynthB: { value: new Color(style.synthColors?.[1] ?? '#FFC93C') },
    },
    vertexShader: SKY_VERTEX, fragmentShader: SKY_FRAGMENT, side: BackSide, depthWrite: false, depthTest: true, fog: false,
  });
  const geo = new SphereGeometry(SKY_RADIUS, 32, 16);
  const mesh = new Mesh(geo, mat);
  mesh.name = 'sky';
  // Last in the opaque pass (it sits at the far plane, depth-tested): only the pixels nothing else
  // covers run the cloud fbm, instead of the whole screen every frame (INT-B).
  mesh.renderOrder = 10;
  mesh.frustumCulled = false;
  return { mesh, dispose: () => { geo.dispose(); mat.dispose(); } };
}

const CONFETTI_VERTEX = /* glsl */ `
attribute vec4 aSeed;
attribute vec3 aColor;
uniform float uTime;
uniform vec3 uCenter;
uniform vec2 uRadius;
uniform vec2 uHeight;
varying vec3 vColor;
varying float vShade;
#include <common>
#include <fog_pars_vertex>
void main() {
  float t = uTime * (0.6 + aSeed.w * 0.5) + aSeed.x * 100.0;
  float span = uHeight.y - uHeight.x;
  float y = uHeight.y - mod(t * 0.55 + aSeed.y * span, span);
  float ang = aSeed.z * 6.2831853 + uTime * 0.02 * (aSeed.w - 0.5);
  float r = mix(uRadius.x, uRadius.y, fract(aSeed.x * 7.13));
  vec3 base = uCenter + vec3(cos(ang) * r, y, sin(ang) * r);
  base.x += sin(t * 1.3) * 0.6;
  base.z += cos(t * 1.1) * 0.6;
  // Flutter: spin about a tumbling axis.
  float a1 = t * 3.1;
  float a2 = t * 2.3 + aSeed.y * 6.0;
  vec3 p = position;
  p = vec3(p.x, p.y * cos(a1), p.y * sin(a1));
  p = vec3(p.x * cos(a2) - p.z * sin(a2), p.y, p.x * sin(a2) + p.z * cos(a2));
  vShade = 0.72 + 0.28 * abs(cos(a1));
  vColor = aColor;
  vec4 mvPosition = modelViewMatrix * vec4(base + p, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const CONFETTI_FRAGMENT = /* glsl */ `
varying vec3 vColor;
varying float vShade;
#include <common>
#include <fog_pars_fragment>
void main() {
  gl_FragColor = vec4(vColor * vShade, 1.0);
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

export const CONFETTI_COUNT: Readonly<Record<QualityTier, number>> = { high: 140, medium: 90, low: 48, potato: 24 };

/** Drifting confetti in a ring band around the arena (outside `r0`, below `y1`). */
export function createConfetti(colors: readonly ColorRepresentation[], tier: QualityTier, seed: number, env: EnvUniforms,
  band: { r0: number; r1: number; y0: number; y1: number }): { mesh: InstancedMesh; dispose(): void } {
  const count = CONFETTI_COUNT[tier];
  const geo = new PlaneGeometry(0.34, 0.2);
  const rnd = seeded(seed * 31 + 7);
  const seeds = new Float32Array(count * 4);
  const cols = new Float32Array(count * 3);
  const c = new Color();
  for (let i = 0; i < count; i++) {
    seeds.set([rnd(), rnd(), rnd(), rnd()], i * 4);
    c.set(colors[i % colors.length]);
    cols.set([c.r, c.g, c.b], i * 3);
  }
  geo.setAttribute('aSeed', new InstancedBufferAttribute(seeds, 4));
  geo.setAttribute('aColor', new InstancedBufferAttribute(cols, 3));
  const mat = new ShaderMaterial({
    uniforms: {
      ...UniformsUtils.clone(UniformsLib.fog), uTime: env.uTime, uCenter: { value: new Vector3(0, 0, 0) },
      uRadius: { value: new Vector2(band.r0, band.r1) }, uHeight: { value: new Vector2(band.y0, band.y1) },
    },
    vertexShader: CONFETTI_VERTEX, fragmentShader: CONFETTI_FRAGMENT, side: DoubleSide, fog: true,
  });
  const mesh = new InstancedMesh(geo, mat, count);
  const id = new Matrix4();
  for (let i = 0; i < count; i++) mesh.setMatrixAt(i, id);
  mesh.frustumCulled = false;
  mesh.name = 'confetti';
  return { mesh, dispose: () => { geo.dispose(); mat.dispose(); } };
}
